/**
 * Extracted Agent Dialog Forms — Isolated state to prevent parent re-renders
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { agentStudentsAPI, agentRequestsAPI, payoutAPI, coursesAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { Plus, Wallet, AlertCircle, Loader2, GraduationCap, Send, Search } from 'lucide-react';

/* ─── Add Student Dialog ─── */
export const AddStudentDialog = ({ open, onOpenChange, onSuccess }) => {
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', nationality: '',
    education_level: '', field_of_study: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await agentStudentsAPI.create(form);
      // Backend returns 200 with already_existed=true if the student is
      // already in the agent's list — surface this clearly.
      if (res?.data?.already_existed) {
        toast(`${res.data.student?.full_name || 'Student'} is already in your list`, { description: 'No duplicate was created.' });
      } else {
        toast.success('Student registered successfully');
      }
      onOpenChange(false);
      setForm({ full_name: '', email: '', phone: '', nationality: '', education_level: '', field_of_study: '', notes: '' });
      onSuccess?.();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to register student'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="text-gray-800 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-teal-600" />
            Register New Student
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-600">Full Name *</Label>
              <Input value={form.full_name} onChange={(e) => update('full_name', e.target.value)} placeholder="Student's full name" className="bg-white border-gray-200 mt-1" data-testid="agent-add-student-name" required />
            </div>
            <div>
              <Label className="text-gray-600">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="student@example.com" className="bg-white border-gray-200 mt-1" data-testid="agent-add-student-email" required />
            </div>
            <div>
              <Label className="text-gray-600">Phone</Label>
              <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+xx xxx xxxx" className="bg-white border-gray-200 mt-1" />
            </div>
            <div>
              <Label className="text-gray-600">Nationality</Label>
              <Input value={form.nationality} onChange={(e) => update('nationality', e.target.value)} placeholder="e.g. Pakistani" className="bg-white border-gray-200 mt-1" />
            </div>
            <div>
              <Label className="text-gray-600">Education Level</Label>
              <Select value={form.education_level} onValueChange={(v) => update('education_level', v)}>
                <SelectTrigger className="bg-white border-gray-200 mt-1"><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high_school">High School</SelectItem>
                  <SelectItem value="bachelors">Bachelor's</SelectItem>
                  <SelectItem value="masters">Master's</SelectItem>
                  <SelectItem value="phd">PhD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-600">Field of Study</Label>
              <Input value={form.field_of_study} onChange={(e) => update('field_of_study', e.target.value)} placeholder="e.g. Computer Science" className="bg-white border-gray-200 mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-gray-600">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder="Any additional notes..." rows={2} className="bg-white border-gray-200 mt-1" data-testid="agent-add-student-notes" />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-teal-600 hover:bg-teal-700" data-testid="agent-submit-student">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Register Student
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/* ─── Payout Request Dialog ─── */
export const PayoutRequestDialog = ({ open, onOpenChange, availableBalance, onSuccess }) => {
  const [form, setForm] = useState({ amount: '', bank_details: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const formatCurrency = (amount) => `£${Number(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form.amount || !form.bank_details) {
      toast.error('Amount and bank details are required');
      return;
    }
    if (Number(form.amount) < 50) {
      toast.error('Minimum payout amount is £50');
      return;
    }
    setSubmitting(true);
    try {
      await payoutAPI.request(form);
      toast.success('Payout request submitted');
      onOpenChange(false);
      setForm({ amount: '', bank_details: '', notes: '' });
      onSuccess?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to submit payout request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="text-gray-800 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-teal-600" />
            Request Payout
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
            <p className="text-gray-500 text-sm mb-1">Available Balance</p>
            <p className="text-2xl font-semibold text-green-600">{formatCurrency(availableBalance)}</p>
          </div>
          <div>
            <Label className="text-gray-600">Amount (£) *</Label>
            <Input type="number" min="50" max={availableBalance} step="0.01" value={form.amount} onChange={(e) => update('amount', e.target.value)} placeholder="Minimum £50" className="bg-white border-gray-200 mt-1" required data-testid="payout-amount" />
            <p className="text-xs text-gray-500 mt-1">Minimum payout: £50</p>
          </div>
          <div>
            <Label className="text-gray-600">Bank Details *</Label>
            <Textarea value={form.bank_details} onChange={(e) => update('bank_details', e.target.value)} placeholder="Account Name, Sort Code, Account Number, Bank Name" rows={3} className="bg-white border-gray-200 mt-1" required data-testid="payout-bank" />
          </div>
          <div>
            <Label className="text-gray-600">Additional Notes</Label>
            <Textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder="Any additional information..." rows={2} className="bg-white border-gray-200 mt-1" data-testid="payout-notes" />
          </div>
          <div className="flex items-start gap-2 p-3 bg-teal-500/10 border border-yellow-500/30 rounded-lg">
            <AlertCircle className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
            <p className="text-amber-700 text-xs">Payouts are typically processed within 3-5 business days.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-teal-600 hover:bg-teal-700" data-testid="submit-payout">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Request
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/* ─── Course Request Dialog ─── */
export const CourseRequestDialog = ({ open, onOpenChange, onSuccess }) => {
  const [form, setForm] = useState({ university_name: '', course_name: '', country: '', degree_level: '', additional_info: '' });
  const [submitting, setSubmitting] = useState(false);
  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form.university_name || !form.course_name) {
      toast.error('University and course name are required');
      return;
    }
    setSubmitting(true);
    try {
      await agentRequestsAPI.create(form);
      toast.success('Course request submitted');
      onOpenChange(false);
      setForm({ university_name: '', course_name: '', country: '', degree_level: '', additional_info: '' });
      onSuccess?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="text-gray-800 flex items-center gap-2">
            <Send className="w-5 h-5 text-teal-600" />
            Request Course/University
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label className="text-gray-600">University Name *</Label>
            <Input value={form.university_name} onChange={(e) => update('university_name', e.target.value)} placeholder="University name" className="bg-white border-gray-200 mt-1" required data-testid="request-university" />
          </div>
          <div>
            <Label className="text-gray-600">Course Name *</Label>
            <Input value={form.course_name} onChange={(e) => update('course_name', e.target.value)} placeholder="Course name" className="bg-white border-gray-200 mt-1" required data-testid="request-course" />
          </div>
          <div>
            <Label className="text-gray-600">Country</Label>
            <Input value={form.country} onChange={(e) => update('country', e.target.value)} placeholder="Country" className="bg-white border-gray-200 mt-1" />
          </div>
          <div>
            <Label className="text-gray-600">Degree Level</Label>
            <Select value={form.degree_level} onValueChange={(v) => update('degree_level', v)}>
              <SelectTrigger className="bg-white border-gray-200 mt-1"><SelectValue placeholder="Select level" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="foundation">Foundation</SelectItem>
                <SelectItem value="bachelors">Bachelor's</SelectItem>
                <SelectItem value="masters">Master's</SelectItem>
                <SelectItem value="phd">PhD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-600">Additional Info</Label>
            <Textarea value={form.additional_info} onChange={(e) => update('additional_info', e.target.value)} placeholder="Any extra details..." rows={2} className="bg-white border-gray-200 mt-1" data-testid="request-info" />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-teal-600 hover:bg-teal-700" data-testid="submit-request">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Request
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
